
import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Calendar, User, UserPlus, Users, Award } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ClientsList } from "./components/ClientsList";
import { AddClientForm } from "./components/AddClientForm";
import { Client } from "./types";
import { useState, useEffect } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useUserRoles } from "@/hooks/useUserRoles";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClientScheduleView } from "./components/ClientScheduleView";
import { ClientPerformanceView } from "./components/ClientPerformanceView";
import { AddBehaviorModal } from "@/components/behavior/AddBehaviorModal";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { useLocation } from "react-router-dom";

const Clients = () => {
  const { toast } = useToast();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const { isAdmin, roles, isSuperAdmin, isLoading: rolesLoading } = useUserRoles();
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [activeTab, setActiveTab] = useState<string>("list");
  const { user } = useSupabaseUser();
  const location = useLocation();
  
  const canAddClient = isAdmin === true;
  const isClientUser = roles?.includes('client');
  const isGuardUser = roles?.includes('guard');
  
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam === 'teams-scheduling') {
      setActiveTab('schedule');
    }
  }, [location]);
  
  const { data: clients, isLoading, refetch: refetchClients } = useQuery({
    queryKey: ['clients', isSuperAdmin, isClientUser, user?.id],
    queryFn: async () => {
      console.log('Fetching clients...');
      
      try {
        const { data: userRolesData, error: userRolesError } = await supabase
          .from('user_roles')
          .select('user_id')
          .eq('role', 'client');
        
        if (userRolesError) {
          console.error('Error fetching client roles:', userRolesError);
          toast({
            title: "Error loading clients",
            description: userRolesError.message,
            variant: "destructive",
          });
          return [];
        }

        console.log('Retrieved user_roles with client role:', userRolesData?.length || 0);
        
        if (!userRolesData || userRolesData.length === 0) {
          console.log('No clients found in user_roles');
          return [];
        }

        const clientUserIds = userRolesData.map(ur => ur.user_id);
        console.log('Client user IDs count:', clientUserIds.length);
        
        const userId = user?.id;
        
        let profileQuery;
        if (isClientUser && userId) {
          console.log('Current user is a client, fetching only their profile');
          profileQuery = supabase
            .from('profiles')
            .select('*')
            .eq('id', userId);
        } else {
          console.log('Fetching client profiles for current admin');
          profileQuery = supabase
            .from('profiles')
            .select('*')
            .in('id', clientUserIds);
          
          // Filter by created_by for non-super admins
          if (!isSuperAdmin && userId) {
            profileQuery = profileQuery.eq('created_by', userId);
          }
        }
        
        const { data: profiles, error: profilesError } = await profileQuery;
        
        if (profilesError) {
          console.error('Error fetching client profiles:', profilesError);
          toast({
            title: "Error loading client data",
            description: profilesError.message,
            variant: "destructive",
          });
          return [];
        }

        console.log('Found client profiles in database:', profiles?.length || 0);
        
        if (!profiles || profiles.length === 0) {
          console.log('No client profiles found');
          return [];
        }
        
        const clientsList: Client[] = profiles.map(profile => {
          return {
            id: profile.id,
            first_name: profile.first_name || '',
            last_name: profile.last_name || '',
            email: profile.email || '',
            mobile_phone: profile.mobile_phone || null,
            status: profile.status || 'active',
            created_at: profile.created_at,
            code: profile.code || null,
            company_name: (profile as any).company_name || null
          };
        });
        
        clientsList.forEach(client => {
          console.log(`Client profile ${client.id}:`, {
            id: client.id,
            firstName: client.first_name,
            lastName: client.last_name,
            email: client.email,
            status: client.status,
            company: client.company_name
          });
        });
        
        if (isClientUser && clientsList.length === 1) {
          const clientData = clientsList[0];
          console.log('Auto-selecting client profile:', clientData);
          
          setTimeout(() => {
            setSelectedClient(clientData);
            setActiveTab("schedule");
          }, 0);
        }
        
        return clientsList;
      } catch (error: any) {
        console.error('Error in client fetch:', error);
        toast({
          title: "Error loading clients",
          description: error.message || "Unknown error occurred",
          variant: "destructive",
        });
        return [];
      }
    },
    enabled: !!user && !rolesLoading,
  });

  useEffect(() => {
    if (isClientUser && selectedClient) {
      setActiveTab("schedule");
    }
  }, [isClientUser, selectedClient]);

  useEffect(() => {
    if (isGuardUser) {
      setActiveTab("schedule");
    }
  }, [isGuardUser]);

  const handleClientAdded = async () => {
    console.log("Client added, refreshing list...");
    await refetchClients();
  };

  const handleClientRemoved = () => {
    if (selectedClient) {
      setSelectedClient(null);
      setActiveTab("list");
    }
    refetchClients();
  };

  const handleClientUpdated = () => {
    refetchClients();
  };
  
  const handleClientSelected = (client: Client) => {
    console.log('Selected client:', client);
    setSelectedClient(client);
    
    setActiveTab("schedule");
  };

  useEffect(() => {
    const redirectClientToTheirProfile = async () => {
      if (isClientUser && user && clients && clients.length > 0) {
        const clientProfile = clients.find(client => client.id === user.id);
        if (clientProfile) {
          setSelectedClient(clientProfile);
          setActiveTab("schedule");
        }
      }
    };
    
    if (clients) {
      redirectClientToTheirProfile();
    }
  }, [clients, isClientUser, user]);

  const getHeaderTitle = () => {
    if (activeTab === 'schedule' || location.search.includes('tab=teams-scheduling')) {
      return "Security Dashboard";
    }
    
    if (isClientUser) {
      return "Security Dashboard";
    } else if (isGuardUser) {
      return "Client Monitoring";
    } else {
      return "Clients Management";
    }
  };

  return (
    <AppLayout>
      <div className="space-y-8 animate-fadeIn p-[30px]">
        <div className="flex justify-between items-center">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold text-secondary-800">
                {getHeaderTitle()}
              </h1>
              {isAdmin && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  <span>{isLoading ? "..." : clients?.length || 0}</span>
                </Badge>
              )}
            </div>
            <p className="text-gray-500">
              {isClientUser 
                ? "View your security schedule and information" 
                : isGuardUser
                  ? "Monitor clients and view their security schedules"
                  : "Manage your clients and their information"}
            </p>
          </div>
          
          {!isClientUser && !isGuardUser && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    {canAddClient ? (
                      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
                        <SheetTrigger asChild>
                          <Button 
                            className="flex items-center gap-2"
                          >
                            <UserPlus className="w-4 h-4" />
                            Add New Client
                          </Button>
                        </SheetTrigger>
                        <SheetContent>
                          <SheetHeader>
                            <SheetTitle>Add New Client</SheetTitle>
                            <SheetDescription>
                              Create a new client profile.
                            </SheetDescription>
                          </SheetHeader>
                          <AddClientForm 
                            onSuccess={handleClientAdded}
                            onClose={() => setIsSheetOpen(false)}
                          />
                        </SheetContent>
                      </Sheet>
                    ) : (
                      <Button 
                        disabled
                        className="flex items-center gap-2 cursor-not-allowed opacity-50"
                      >
                        <UserPlus className="w-4 h-4" />
                        Add New Client
                      </Button>
                    )}
                  </div>
                </TooltipTrigger>
                {!canAddClient && (
                  <TooltipContent>
                    <p>Only administrators can add new clients</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            {isAdmin && (
              <TabsTrigger value="list" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <span>Clients List</span>
              </TabsTrigger>
            )}
            {(selectedClient || isClientUser) && (
              <TabsTrigger value="schedule" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>Guard Monitoring</span>
              </TabsTrigger>
            )}
            {(selectedClient || isClientUser) && (
              <TabsTrigger value="performance" className="flex items-center gap-2">
                <Award className="h-4 w-4" />
                <span>Performance & Evaluations</span>
              </TabsTrigger>
            )}
          </TabsList>
          
          {isAdmin && (
            <TabsContent value="list">
              <ClientsList 
                clients={clients} 
                isLoading={isLoading} 
                onClientRemoved={handleClientRemoved}
                onClientUpdated={handleClientUpdated}
                onClientSelected={handleClientSelected}
                isGuardView={isGuardUser}
              />
            </TabsContent>
          )}
          
          <TabsContent value="schedule">
            {selectedClient || isClientUser ? (
              <ClientScheduleView clientId={selectedClient?.id || user?.id || ""} />
            ) : (
              <div className="text-center py-12 bg-muted rounded-lg">
                <p className="text-muted-foreground">
                  Select a client to view their security schedule
                </p>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="performance">
            {selectedClient || isClientUser ? (
              <ClientPerformanceView clientId={selectedClient?.id || user?.id || ""} />
            ) : (
              <div className="text-center py-12 bg-muted rounded-lg">
                <p className="text-muted-foreground">
                  Select a client to view performance reports and evaluations
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
};

export default Clients;
